import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import ScrollToTop from './Components/ScrollToTop/ScrollToTop';
import Loader from './Components/Loading/Loading';

// Componentes com carregamento dinâmico
const Home = lazy(() => import('./Pages/Home'));
const Sobre = lazy(() => import('./Pages/Sobre/Sobre'));
const SejaFranqueado = lazy(() => import('./Pages/SejaFranqueado/SejaFranqueado'));
const FaleConosco = lazy(() => import('./Pages/FaleConosco'));
const Cardapio = lazy(() => import('./Pages/Cardapio'));
const Combos = lazy(() => import('./Pages/Combos'));
const Cupons = lazy(() => import('./Pages/Cupons'));
const NossaHistoria = lazy(() => import('./Pages/NossaHistoria'));
const Ofertas = lazy(() => import('./Pages/Ofertas'));
const Franqueadora = lazy(() => import('./Pages/Franqueadora'));
const TrabalheConosco = lazy(() => import('./Pages/TrabalheConosco'));
const TermosDeUso = lazy(() => import('./Pages/TermosDeUso'));
const TermosCupons = lazy(() => import('./Pages/TermosCupons'));
const PoliticaPrivacidade = lazy(() => import('./Pages/PoliticaPrivacidade'));
const BaixeApp = lazy(() => import('./Pages/BaixeApp'));
const RaspadinhaDigital = lazy(() => import('./Components/Raspadinha/Raspadinha'));
const AdsTest = lazy(() => import('./Pages/AdsTest'));
const DesignSystem = lazy(() => import('./Pages/DesignSystem'));
const Lojas = lazy(() => import('./Pages/Lojas/Lojas'));
const Loja = lazy(() => import('./Pages/Lojas/Loja'));
const TermsConditionsRaspadinha = lazy(() => import('./Components/Termos/TermsConditionsRaspadinha'));
const PageNotFound = lazy(() => import('./Pages/PageNotFound'));
const RedirectToApp = lazy(() => import('./Pages/RedirectToApp'));

// Landing Page
const LPSummit25 = lazy(() => import('./LandingPage/Summit2025/public/index'));

// Posts SEO
const PostPizzaCalabresaPepperoni = lazy(() => import('./Components/TopPosts/PostPizzaCalabresaPepperoni'));
const PostOrigemPizzaCaprese = lazy(() => import('./Components/TopPosts/PostOrigemPizzaCaprese'));
const PostBeneficiosPresuntoParma = lazy(() => import('./Components/TopPosts/PostBeneficiosPresuntoParma'));
const PostPizzaPertoDeMim = lazy(() => import('./Components/TopPosts/PostPizzaPertoDeMim'));

const AppRoutes = () => {

  // Função de redirecionamento para URLs externas
  const ExternalRedirect = () => {
    const location = useLocation();
    useEffect(() => {
      if (location.pathname === '/blog') {
        window.location.href = 'https://blog.pizzaprime.com.br';
      }
    }, [location.pathname]);
  
    return null;
  };

  // Função para simplificar o redirecionamento de URLs antigas
  const renderRedirect = (from, to) => (
    <Route path={from} element={<Navigate to={to} replace />} />
  );

  return (
    <Router>
      <ScrollToTop />
      <Suspense fallback={<Loader />}>
        <Routes>
          {/* Redirecionamentos de unidades antigas */}
          {renderRedirect("/unidades/*", "/lojas")}
          {renderRedirect("/unidades/rio-grande-do-sul/novo-hamburgo", "/novo-hamburgo")}
          {renderRedirect("/unidades/rio-grande-do-sul/pelotas", "/pelotas")}
          {renderRedirect("/unidades/rio-grande-do-sul/rio-grande", "/rio-grande")}
          {renderRedirect("/unidades/sao-paulo/vila-matilde", "/vila-matilde")}
          {renderRedirect("/unidades/sao-paulo/campo-belo", "/campo-belo")}
          {renderRedirect("/unidades/sao-paulo/osasco", "/osasco")}
          {renderRedirect("/unidades/sao-paulo/tatui", "/tatui")}
          {renderRedirect("/unidades/sao-paulo/salto", "/salto")}
          {renderRedirect("/unidades/sao-paulo/bela-vista-paulista", "/bela-vista")}
          {renderRedirect("/unidades/sao-paulo/aclimacao-ipiranga", "/aclimacao")}
          {renderRedirect("/unidades/sao-paulo/campinas-cambui", "/campinas")}
          {renderRedirect("/unidades/sao-paulo/ribeirao-preto", "/ribeirao-preto")}
          {renderRedirect("/unidades/sao-paulo/mogi-das-cruzes", "/mogi-das-cruzes")}
          {renderRedirect("/unidades/sao-paulo/valinhos", "/valinhos")}
          {renderRedirect("/unidades/santa-catarina/itapema", "/itapema")}
          {renderRedirect("/unidades/santa-catarina/chapeco", "/chapeco")}
          {renderRedirect("/unidades/santa-catarina/itajai", "/chapeco")}
          {renderRedirect("/unidades/santa-catarina/criciuma", "/criciuma")}
          {renderRedirect("/unidades/santa-catarina/balneario-camboriu", "/balneario-camboriu")}

          {/* Redirecionamentos para URLs antigas do blog */}
          {renderRedirect("/cardapio-pizza-prime/*", "/cardapio")}
          {renderRedirect("/tag/*", "/")}
          {renderRedirect("/categoria/*", "/")}
          {renderRedirect("/onde-comer-pizza/*", "/")}
          {renderRedirect("/pizza-em-casa/*", "/")}
          {renderRedirect("/aplicativo-pizza-prime/*", "/")}
          {renderRedirect("/pedido_de_pizza_online/*", "/")}
          <Route path="/blog/*" element={<ExternalRedirect />} />

          {/* Outras rotas */}
          <Route path="/" element={<Home />} />
          <Route path="/sobre" element={<Sobre />} />
          <Route path="/franquia-de-pizzaria" element={<SejaFranqueado />} />
          <Route path="/fale-conosco" element={<FaleConosco />} />
          <Route path="/cardapio" element={<RedirectToApp />} />
          <Route path="/combos" element={<Combos />} />
          <Route path="/cupons" element={<Cupons />} />
          <Route path="/nossa-historia" element={<NossaHistoria />} />
          <Route path="/ofertas" element={<Ofertas />} />
          <Route path="/franqueadora" element={<Franqueadora />} />
          <Route path="/trabalhe-conosco" element={<TrabalheConosco />} />
          <Route path="/termos-de-uso" element={<TermosDeUso />} />
          <Route path="/termos-cupons" element={<TermosCupons />} />
          <Route path="/politica-privacidade" element={<PoliticaPrivacidade />} />
          <Route path="/app/download" element={<BaixeApp />} />
          <Route path="/raspadinha-digital" element={<RaspadinhaDigital />} />
          <Route path="/ads" element={<AdsTest />} />
          <Route path="/lojas" element={<Lojas />} />
          <Route path="/:parametro" element={<Loja />} />
          <Route path="/DesignSystem" element={<DesignSystem />} />
          <Route path="*" element={<PageNotFound />} />
          <Route path="/promocao/raspadinha/termos" element={<TermsConditionsRaspadinha />} />
          <Route path="/redirectToApp" element={<RedirectToApp />} />

          {/* Posts SEO */}
          <Route path='/pizza-de-calabresa-e-pizza-de-pepperoni' element={<PostPizzaCalabresaPepperoni />} />
          <Route path='/origem-pizza-caprese' element={<PostOrigemPizzaCaprese />} />
          <Route path='/beneficios-do-presunto-parma' element={<PostBeneficiosPresuntoParma />} />
          <Route path='/pizza-perto-de-mim' element={<PostPizzaPertoDeMim />} />

          {/* Páginas adicionais */}
          <Route path="/perguntas-frequentes" element={<Home />} />
          <Route path="/termos-e-condicoes" element={<Home />} />
          <Route path="/pizza-prime-delivery" element={<Home />} />
          <Route path="/terca-em-dobro" element={<Home />} />
          <Route path="/clipping" element={<Home />} />

          {/* Landings Pages */}
          <Route path="/summit/25" element={<LPSummit25 />} />
        </Routes>
      </Suspense>
    </Router>
  );
};

export default AppRoutes;
